<template>
  <div>
    <div class="card">
      <div class="card__head">
        <div>
          <h2 class="card__title">{{ t("usage.title") }}</h2>
          <p class="card__sub">{{ t("usage.lede") }}</p>
        </div>
        <div class="row">
          <select v-model.number="days" class="select usage__days" :aria-label="t('usage.period')" @change="load">
            <option v-for="d in [7, 30, 90]" :key="d" :value="d">{{ t("usage.lastDays", { count: d }) }}</option>
          </select>
          <button type="button" class="btn btn--secondary btn--sm" :disabled="loading" @click="load">{{ t("common.refresh") }}</button>
        </div>
      </div>

      <div v-if="error" class="notice notice--danger"><div class="notice__body">{{ error }}</div></div>
      <div v-else-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>
      <p v-else-if="!rows.length" class="muted small">{{ t("usage.empty") }}</p>

      <template v-else>
        <div class="grid grid--3 usage__totals">
          <div><div class="xs muted">{{ t("usage.calls") }}</div><div class="usage__big">{{ n(totals.calls) }}</div></div>
          <div>
            <div class="xs muted">{{ t("usage.errors") }}</div>
            <div class="usage__big">{{ n(totals.errors) }} <span class="small muted">{{ pct(totals.errors, totals.calls) }}</span></div>
          </div>
          <div><div class="xs muted">{{ t("usage.rateLimited") }}</div><div class="usage__big">{{ n(totals.rate_limited) }}</div></div>
        </div>

        <h3 class="usage__h">{{ t("usage.byDay") }}</h3>
        <div class="table__wrap">
          <table class="table">
            <thead>
              <tr><th>{{ t("usage.day") }}</th><th>{{ t("usage.calls") }}</th><th></th><th>{{ t("usage.errors") }}</th><th>{{ t("usage.rateLimited") }}</th><th>{{ t("usage.p95") }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="d in byDay" :key="d.key">
                <td class="small">{{ formatDate(d.key, false) }}</td>
                <td class="mono xs">{{ n(d.calls) }}</td>
                <td class="usage__barcell"><span class="usage__bar" :style="{ width: `${barWidth(d.calls)}%` }"></span></td>
                <td class="mono xs">{{ n(d.errors) }}</td>
                <td class="mono xs">{{ n(d.rate_limited) }}</td>
                <td class="mono xs">{{ d.p95_ms != null ? `${d.p95_ms} ms` : "—" }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <template v-if="byInstallation.length">
          <h3 class="usage__h">{{ t("usage.byInstallation") }}</h3>
          <div class="table__wrap">
            <table class="table">
              <thead><tr><th>{{ t("testers.installation") }}</th><th>{{ t("usage.calls") }}</th><th>{{ t("usage.errors") }}</th><th>{{ t("usage.rateLimited") }}</th></tr></thead>
              <tbody>
                <tr v-for="r in byInstallation" :key="r.key">
                  <td class="small">{{ installationName(installations, r.key) }}</td>
                  <td class="mono xs">{{ n(r.calls) }}</td>
                  <td class="mono xs">{{ n(r.errors) }}</td>
                  <td class="mono xs">{{ n(r.rate_limited) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-if="byEndpoint.length">
          <h3 class="usage__h">{{ t("usage.byEndpoint") }}</h3>
          <div class="table__wrap">
            <table class="table">
              <thead><tr><th>{{ t("usage.endpoint") }}</th><th>{{ t("usage.calls") }}</th><th>{{ t("usage.errors") }}</th></tr></thead>
              <tbody>
                <tr v-for="r in byEndpoint" :key="r.key">
                  <td><code class="xs">{{ r.key }}</code></td>
                  <td class="mono xs">{{ n(r.calls) }}</td>
                  <td class="mono xs">{{ n(r.errors) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText, formatDate } from "../../lib/errors";
import { loadInstallations, installationName } from "../../lib/installations";

const props = defineProps({ appId: { type: String, required: true } });
const { t, locale } = useI18n();
const errorText = useErrorText();

const days = ref(7);
const rows = ref([]);
const installations = ref([]);
const loading = ref(true);
const error = ref("");

const num = (v) => Number(v) || 0;
const n = (v) => num(v).toLocaleString(locale.value);
const pct = (part, whole) => (whole ? `${((part / whole) * 100).toFixed(1)}%` : "");

function group(field) {
  const map = new Map();
  for (const r of rows.value) {
    const key = r[field];
    if (key === undefined || key === null) continue;
    const acc = map.get(key) || { key, calls: 0, errors: 0, rate_limited: 0, p95_ms: null };
    acc.calls += num(r.calls);
    acc.errors += num(r.errors);
    acc.rate_limited += num(r.rate_limited);
    if (r.p95_ms != null) acc.p95_ms = Math.max(acc.p95_ms ?? 0, num(r.p95_ms));
    map.set(key, acc);
  }
  return [...map.values()];
}

const totals = computed(() =>
  rows.value.reduce(
    (acc, r) => ({ calls: acc.calls + num(r.calls), errors: acc.errors + num(r.errors), rate_limited: acc.rate_limited + num(r.rate_limited) }),
    { calls: 0, errors: 0, rate_limited: 0 }
  )
);
const byDay = computed(() => group("period").sort((a, b) => String(b.key).localeCompare(String(a.key))));
const byInstallation = computed(() => group("installation_id").sort((a, b) => b.calls - a.calls));
const byEndpoint = computed(() => group("endpoint_key").sort((a, b) => b.calls - a.calls).slice(0, 20));
const maxDay = computed(() => Math.max(1, ...byDay.value.map((d) => d.calls)));
const barWidth = (calls) => Math.round((calls / maxDay.value) * 100);

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [data, list] = await Promise.all([api.usage(props.appId, days.value), loadInstallations().catch(() => [])]);
    rows.value = Array.isArray(data) ? data : data?.rows || [];
    installations.value = list;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.usage__days { width: auto; }
.usage__totals { margin: var(--space-4) 0; }
.usage__big { font-family: var(--font-display); font-size: var(--text-lg); color: var(--ink-strong); }
.usage__h { margin-top: var(--space-5); font-size: var(--text-sm); color: var(--ink-muted); text-transform: uppercase; letter-spacing: .04em; }
.usage__barcell { width: 30%; vertical-align: middle; }
.usage__bar { display: block; height: 6px; min-width: 2px; border-radius: var(--radius-pill); background: var(--accent); }
</style>
